import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboardCheck, faLock, faLeaf, faCertificate } from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";
import companyService from "@/services/companyService";
import { copyTasksToCustomerAssurance } from "@/services/frameworkService";
import { toast } from "sonner";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue
} from "@/components/ui/select";

import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group"
import { Label } from "@/components/ui/label"
import { useTenancy } from '@/contexts/TenancyContext';

// Utility to map Firestore data <-> UI state for ISO 27701
function iso27701ArrayToState(arr, active) {
  const get = (name) => arr?.find(e => e.settingName === name)?.value || "";
  return {
    enabled: active ? "enabled" : "disabled",
    privacyOfficer: get("Privacy Officer Details"),
    piiController: get("PII Controller and/or Processor"),
    processorRole: get("Processor Role"), // 'processor', 'controller', 'both'
  };
}
function iso27701StateToArray(state) {
  return [
    { settingName: "Privacy Officer Details", value: state.privacyOfficer },
    { settingName: "PII Controller and/or Processor", value: state.piiController },
    { settingName: "Processor Role", value: state.processorRole },
  ];
}

function ISO27701Module() {
  const { activeTenancy } = useTenancy();

  const [iso27701, setIso27701] = useState({
    enabled: "disabled",
    privacyOfficer: "",
    piiController: "",
    processorRole: "",
  });
  const [iso27701Initial, setIso27701Initial] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchISO = async () => {
      setIsLoading(true);
      try {
        const settings = await companyService.getCompanySettings(undefined, "complianceModules");
        const isoArr = settings?.["iso27701"] || [];
        const arr = Array.isArray(isoArr) ? isoArr : Object.values(isoArr).filter(x => typeof x === 'object' && x.settingName);
        let isoActive = settings?.["iso27701"]?.active ?? false;
        if (!isoActive) {
          const enabledEntry = arr.find(entry => entry?.settingName === "Enabled" && (entry?.value === true || entry?.value === "true"));
          isoActive = !!enabledEntry;
        }
        const state = iso27701ArrayToState(arr, isoActive);
        setIso27701(state);
        setIso27701Initial(state);
        console.log('[ADS]', state);
      } catch (err) {
        console.error("Failed to fetch ISO 27701 settings:", err);
        toast.error("Failed to load ISO 27701 settings");
      } finally {
        setIsLoading(false);
      }
    };
    fetchISO();
  }, []);

  const isoChanged = JSON.stringify(iso27701) !== JSON.stringify(iso27701Initial);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const wasDisabled = iso27701Initial?.enabled === "disabled";
      const isNowEnabled = iso27701.enabled === "enabled";
      
      const isoArr = iso27701StateToArray(iso27701);
      const isoActive = iso27701.enabled === "enabled";
      await companyService.saveCompanySettings(undefined, "complianceModules", {
        "iso27701": {
          ...(isoArr.reduce((acc, item, idx) => ({ ...acc, [idx]: item }), {})),
          active: isoActive,
          Enabled: {
            value: isoActive,
            settingName: "Enabled",
            details: null
          }
        }
      });
      
      // DED-1511: If ISO 27701 is being enabled (was disabled, now enabled), copy tasks
      if (wasDisabled && isNowEnabled && activeTenancy?.id) {
        try {
          const customerId = activeTenancy.id;
          
          toast.info("Copying ISO 27701 tasks...");
          
          await copyTasksToCustomerAssurance(
            customerId,
            'ISO27701',
            undefined,
            undefined
          );
          
          toast.success("ISO 27701 settings saved and tasks copied");
        } catch (taskError) {
          console.error('Error copying ISO 27701 tasks:', taskError);
          toast.warning("Settings saved, but there was an error copying some tasks. Please check the console.");
        }
      } else {
        toast.success("ISO 27701 settings saved");
      }
      
      setIso27701Initial({ ...iso27701 });
    } catch (err) {
      console.error("Failed to save ISO 27701 settings:", err);
      toast.error("Failed to save ISO 27701 settings");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* <div className="flex flex-col gap-2 mb-4">
        <div className="flex items-center gap-2 text-orange-600"><FontAwesomeIcon icon={faClipboardCheck} /> <b>87 Controls in your SOA</b></div>
        <div className="flex items-center gap-2 text-orange-600"><FontAwesomeIcon icon={faLeaf} /> <b>31 New and 7 Modified Assurance Tasks</b></div>
        <div className="flex items-center gap-2 text-orange-600"><FontAwesomeIcon icon={faLock} /> <b>1 Data Register in Key Documents</b></div>
        <div className="flex items-center gap-2 text-orange-600"><FontAwesomeIcon icon={faClipboardCheck} /> <b>19 Update Policies</b></div>
        <div className="flex items-center gap-2 text-orange-600"><FontAwesomeIcon icon={faCertificate} /> <b>1 New Policy</b></div>
      </div> */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-foreground">Module Enabled</label>
        <Select
          value={iso27701.enabled}
          onValueChange={val => setIso27701(v => ({ ...v, enabled: val }))}
          disabled={isLoading || isSaving}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="enabled">Enabled</SelectItem>
            <SelectItem value="disabled">Disabled</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Indicates whether the ISO 27701 Module is enabled or disabled.<br />For example, if set to "Enabled," the ISO
          27701 Module is active.
        </p>
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-foreground">Privacy Officer Details</label>
        <textarea
          className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground resize-none"
          rows={3}
          value={iso27701.privacyOfficer}
          onChange={e => setIso27701(v => ({ ...v, privacyOfficer: e.target.value }))}
          disabled={isLoading || isSaving}
        />
        <p className="text-xs text-muted-foreground">
          Contact information for the designated privacy officer.<br />For example:<br />Name: Harold Quackmore the
          3rd<br />Email: harold.quackmore3@deiterate.com<br />Phone: +69 0412 345 678
        </p>
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-foreground">{activeTenancy.legalName} is a:</label>
        {!isLoading && (
          <RadioGroup defaultValue={iso27701.processorRole} onValueChange={value => setIso27701(prev => ({ ...prev, processorRole: value }))}>
            <div className="flex items-center gap-3">
              <RadioGroupItem value="processor" id="processor" />
              <Label htmlFor="processor">Data Processor</Label>
            </div>
            <div className="flex items-center gap-3">
              <RadioGroupItem value="controller" id="controller" />
              <Label htmlFor="controller">Data Controller</Label>
            </div>
            <div className="flex items-center gap-3">
              <RadioGroupItem value="both" id="both" />
              <Label htmlFor="both">Data Processor & Controller</Label>
            </div>
          </RadioGroup>
        )}
      </div>
      <div className="pt-4">
        <button
          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors flex items-center gap-2"
          onClick={handleSave}
          disabled={isSaving || !isoChanged}
        >
          {isSaving && (
            <svg className="animate-spin h-4 w-4 mr-2 text-primary-foreground" xmlns="http://www.w3.org/2000/svg"
                 fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
            </svg>
          )}
          Save ISO 27701 Settings
        </button>
      </div>
    </div>
  );
}

export default ISO27701Module;
export {
  ISO27701Module
}
